import { yupResolver } from '@hookform/resolvers/yup';
import { TextField, Typography, MenuItem, Box, Switch } from '@mui/material';
import { Button, LoadingButton } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useIntl } from '@apigrowfinance/pbolt-intl-provider';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';

import { ILocationFormInputs } from '@clients/accountsClient';
import locationTypes from '@messages/locationTypes';
import locationValidations from '@messages/locationValidations';
import countryDropdownList from '@utils/countryDropdownList';
import { getCountryTranslations } from '@utils/countryTranslations';
import getEnumTranslation from '@utils/getEnumTranslation';
interface LocationDetailsFormProps {
    closeModal: () => void;
    readyToSubmitData: (data: object | null) => void;
    formDefaults: ILocationFormInputs;
    formAction: string;
    isLoading?: boolean;
}

const schema = yup.object({
    type: yup.string().required('LOCATIONSDETAILSFORMTYPE'),
    address: yup.string().required('LOCATIONSDETAILSFORMADDERSS'),
    complementary_address: yup.string(),
    city: yup.string().required('LOCATIONSDETAILSFORMCITY'),
    neighborhood: yup.string().required('LOCATIONSDETAILSFORMNEIGHBORHOOD'),
    zip_code: yup
        .string()
        .required('LOCATIONSDETAILSFORMZIP_CODEREQUIRED')
        .max(9, 'LOCATIONSDETAILSFORMZIP_CODELENGTH'),
    country: yup.string().required('LOCATIONSDETAILSFORMCOUNTRY'),
    mailing_address: yup.boolean(),
});

const LocationDetailsForm = ({
    closeModal,
    readyToSubmitData,
    formDefaults,
    formAction,
    isLoading,
}: LocationDetailsFormProps) => {
    const { locale } = useIntl();
    const { theme } = useTheme();
    const {
        handleSubmit,
        control,
        formState: { isDirty, errors },
    } = useForm<ILocationFormInputs>({
        resolver: yupResolver(schema),
        defaultValues: formDefaults,
    });

    const onSubmit: SubmitHandler<ILocationFormInputs> = (
        data: ILocationFormInputs,
    ) => {
        readyToSubmitData(data);
    };

    const countires = countryDropdownList();

    const typeOptions = [
        {
            label: 'Residential',
            value: 'RESIDENTIAL',
        },
        {
            label: 'Commercial',
            value: 'COMMERCIAL',
        },
        {
            label: 'Other',
            value: 'OTHER',
        },
    ];

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="location-details-form"
            style={{ width: '100%' }}>
            <PDialogContent>
                <Controller
                    name="type"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            error={!!errors.type}
                            helperText={
                                !!errors.type &&
                                getEnumTranslation(errors.type?.message, {
                                    messages: locationValidations,
                                })
                            }
                            id="location-form-type"
                            fullWidth
                            select
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.type"
                                    defaultMessage="Type"
                                />
                            }
                            onChange={onChange}
                            value={value || ''}>
                            {typeOptions?.map((option) => (
                                <MenuItem
                                    key={option.label}
                                    value={option.value}>
                                    {getEnumTranslation(option.value, {
                                        messages: locationTypes,
                                    })}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-evenly">
                    <Controller
                        name="address"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                sx={{ mr: 2 }}
                                error={!!errors.address}
                                helperText={
                                    errors.address &&
                                    getEnumTranslation(
                                        errors.address?.message,
                                        {
                                            messages: locationValidations,
                                        },
                                    )
                                }
                                fullWidth
                                label={
                                    <FormattedMessage
                                        id="locationDetailsForm.address1"
                                        defaultMessage="Address Line 1"
                                    />
                                }
                            />
                        )}
                    />
                    <Controller
                        name="complementary_address"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={
                                    <FormattedMessage
                                        id="locationDetailsForm.complementary_address"
                                        defaultMessage="Address Line 2"
                                    />
                                }
                            />
                        )}
                    />
                </Box>
                <Controller
                    name="city"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="location-form-city"
                            fullWidth
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.city"
                                    defaultMessage="City"
                                />
                            }
                            error={!!errors.city}
                            helperText={
                                errors.city &&
                                getEnumTranslation(errors.city?.message, {
                                    messages: locationValidations,
                                })
                            }
                        />
                    )}
                />
                <Controller
                    name="neighborhood"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="location-form-county"
                            fullWidth
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.neighborhood"
                                    defaultMessage="Province/County"
                                />
                            }
                            error={!!errors.neighborhood}
                            helperText={
                                errors.neighborhood &&
                                getEnumTranslation(
                                    errors.neighborhood?.message,
                                    {
                                        messages: locationValidations,
                                    },
                                )
                            }
                        />
                    )}
                />
                <Controller
                    name="zip_code"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="location-form-zip-code"
                            fullWidth
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.zip_code"
                                    defaultMessage="Zip/Postal code"
                                />
                            }
                            error={!!errors.zip_code}
                            helperText={
                                errors.zip_code &&
                                getEnumTranslation(errors.zip_code?.message, {
                                    messages: locationValidations,
                                })
                            }
                        />
                    )}
                />

                <Controller
                    name="country"
                    defaultValue=""
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            sx={{ mb: 2 }}
                            id="location-form-country"
                            fullWidth
                            select
                            label={
                                <FormattedMessage
                                    id="locationDetailsForm.country"
                                    defaultMessage="Country"
                                />
                            }
                            error={!!errors.country}
                            helperText={
                                errors.country &&
                                getEnumTranslation(errors.country?.message, {
                                    messages: locationValidations,
                                })
                            }
                            onChange={onChange}
                            value={value || ''}>
                            {countires?.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {`${option.emoji} `}
                                    {getCountryTranslations(option.id, locale)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
            </PDialogContent>
            <PDialogContent>
                <Box
                    display="flex"
                    flexDirection="row"
                    alignContent="center"
                    justifyContent="space-between">
                    <Box>
                        <Typography
                            data-testid="location-form-primary-heading"
                            variant="h6"
                            color={
                                theme?.custom?.brand?.textMain || 'text.primary'
                            }>
                            {formAction === 'ADD' ? (
                                <FormattedMessage
                                    id="locationDetailsForm.switchAddTitle"
                                    defaultMessage="This will be set as the primary address"
                                />
                            ) : (
                                <FormattedMessage
                                    id="locationDetailsForm.switchTitle"
                                    defaultMessage="Make this the primary address"
                                />
                            )}
                        </Typography>
                        <Typography
                            data-testid="location-form-primary-text"
                            color={theme.palette.text.secondary}
                            sx={{ fontSize: '0.75rem' }}>
                            <FormattedMessage
                                id="locationDetailsForm.switchText"
                                defaultMessage="This will be the address that all new cards get sent to."
                            />
                        </Typography>
                    </Box>
                    <Box>
                        <Box
                            sx={{
                                width: '20%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'flex-end',
                            }}>
                            <Controller
                                control={control}
                                name="mailing_address"
                                defaultValue={undefined}
                                render={({ field }) => (
                                    <>
                                        {formAction === 'ADD' ? (
                                            <Switch
                                                {...field}
                                                disabled
                                                checked
                                            />
                                        ) : (
                                            <Switch {...field} />
                                        )}
                                    </>
                                )}
                            />
                        </Box>
                    </Box>
                </Box>
            </PDialogContent>
            <PDialogAction>
                <Button onClick={closeModal}>
                    <FormattedMessage
                        id="locationDetailsForm.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
                <LoadingButton loading={isLoading} disabled={!isDirty}>
                    {formAction === 'ADD' ? (
                        <FormattedMessage
                            id="locationsDetailsForm.add"
                            defaultMessage="Add"
                        />
                    ) : (
                        <FormattedMessage
                            id="locationsDetailsForm.save"
                            defaultMessage="Save"
                        />
                    )}
                </LoadingButton>
            </PDialogAction>
        </form>
    );
};

export default LocationDetailsForm;
